import { useCallback } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useDataSource } from '../../../lib/dataSource'
import type { Section, Task } from '../../../lib/db/models'
import { moveBySteps } from '../utils/moveBySteps'
import type { OptimisticUpdates } from './useTaskDragAndDrop'

/**
 * **⌥↑ / ⌥↓ in der Liste** (#204, Unter-Plan Tastatur): die Auswahl um eine Stelle. Sofort
 * sichtbar über dieselben optimistischen Werte wie beim Ziehen, damit nichts springt, bis die
 * Datenbank nachzieht. Reihenfolge ist keine rücknehmbare Handlung (ADR 0028) — kein ⌘Z.
 *
 * @param crossSections über Abschnittsgrenzen hinweg; aus in Kurz notiert (gedachte Abschnitte)
 */
export function useKeyboardMove({ segments, setOptimisticUpdates, crossSections }: {
  segments: { section: Section | null; tasks: Task[] }[]
  setOptimisticUpdates: Dispatch<SetStateAction<OptimisticUpdates>>
  crossSections: boolean
}): (ids: string[], direction: -1 | 1) => void {
  const source = useDataSource()
  return useCallback((ids: string[], direction: -1 | 1) => {
    const updates = moveBySteps(segments, new Set(ids), direction, { crossSections })
    if (!updates || updates.length === 0) return
    setOptimisticUpdates(prev => {
      const next = new Map(prev ?? [])
      for (const u of updates) next.set(u.id, u)
      return next
    })
    void source.reorderTasks(updates)
  }, [segments, setOptimisticUpdates, crossSections, source])
}
